import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import NewsCard from "@/components/base/NewsCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { comicCategories, comics } from "@/mocks/comics";
import { news } from "@/mocks/news";

const sortedComics = [...comics].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
const latestUpdates = sortedComics.slice(0, 6);
const featured = comics.slice(0, 5);
const latestNews = [...news].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);

export default function Home() {
  useDocumentMeta({
    title: "妖精漫画-精彩漫画作品与最新资讯平台",
    description:
      "妖精漫画为您提供丰富的妖精题材漫画作品和最新资讯，涵盖多类型妖精题材内容，满足漫画爱好者的阅读和了解需求。",
    keywords: "妖精漫画,妖精题材漫画,漫画作品,漫画资讯,妖精故事,漫画推荐,漫画阅读",
  });

  return (
    <div className="w-full">
      <HeroCarousel items={featured} />

      {/* 分类快捷入口 */}
      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">探索妖精世界</h2>
            <p className="mt-2 text-sm text-foreground-600">从你喜欢的妖精分类出发，快速找到心仪的作品。</p>
          </div>
          <Link
            to="/category"
            className="flex items-center gap-1 text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            全部分类
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {comicCategories.map((cat) => (
            <Link
              key={cat.key}
              to={`/category?cat=${encodeURIComponent(cat.name)}`}
              className="group flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-5 text-center transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                <i className={`${cat.icon} text-2xl`}></i>
              </span>
              <span className="text-sm font-medium text-foreground-900">{cat.name}</span>
              <span className="line-clamp-2 text-xs leading-relaxed text-foreground-500">{cat.desc}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* 左右分栏 */}
      <section className="mx-auto max-w-[1280px] px-4 pb-12 md:px-6 md:pb-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* 左侧主内容 */}
          <div className="flex flex-col gap-8 lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="mb-5 flex items-center justify-between gap-4">
                <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950 md:text-2xl">
                  <i className="ri-refresh-line text-primary-600"></i>
                  最新漫画更新
                </h2>
                <Link
                  to="/category"
                  className="flex items-center gap-1 text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer whitespace-nowrap"
                >
                  更多更新
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <ul className="flex flex-col divide-y divide-background-200">
                {latestUpdates.map((comic, i) => (
                  <li key={comic.id}>
                    <Link
                      to={`/detail/${comic.id}`}
                      className="group flex items-center gap-4 py-3 cursor-pointer"
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm font-medium ${
                          i < 3
                            ? "bg-accent-100 text-accent-700"
                            : "bg-background-100 text-foreground-500"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <div className="relative hidden h-16 w-12 shrink-0 overflow-hidden rounded-md sm:block">
                        <img
                          src={comic.cover}
                          alt={`${comic.title} 封面`}
                          title={`${comic.title} 妖精题材漫画`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600">
                          {comic.title}
                        </h3>
                        <p className="mt-1 truncate text-xs text-foreground-500">
                          {comic.author} · {comic.category} · {comic.status}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-xs text-primary-600 whitespace-nowrap">
                          第 {comic.latestChapter} 话
                        </p>
                        <p className="mt-1 text-xs text-foreground-400 whitespace-nowrap">{comic.updatedAt}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl text-foreground-950 md:text-2xl">
                <i className="ri-sparkling-line text-accent-500"></i>
                精选推荐
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {featured.slice(0, 3).map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/detail/${comic.id}`}
                    className="group flex flex-col gap-3 cursor-pointer"
                  >
                    <div className="relative w-full overflow-hidden rounded-md pt-[133%]">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} 封面`}
                        title={`${comic.title} 妖精题材漫画`}
                        className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600">
                      {comic.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* 右侧栏 */}
          <aside className="flex flex-col gap-8">
            <div className="rounded-lg border border-background-200 bg-secondary-50 p-5 md:p-6">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className="ri-compasses-2-line text-secondary-700"></i>
                快捷分类导航
              </h2>
              <div className="flex flex-wrap gap-2">
                {comicCategories.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category?cat=${encodeURIComponent(cat.name)}`}
                    className="flex items-center gap-1.5 rounded-full border border-secondary-200 bg-background-50 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-secondary-400 hover:text-secondary-900 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-secondary-600`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="mb-4 flex items-center justify-between gap-4">
                <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                  <i className="ri-newspaper-line text-primary-600"></i>
                  最新资讯
                </h2>
                <Link
                  to="/news"
                  className="text-xs text-primary-600 transition-colors hover:text-primary-700 cursor-pointer whitespace-nowrap"
                >
                  全部
                </Link>
              </div>
              <div className="flex flex-col gap-3">
                {latestNews.map((item) => (
                  <NewsCard key={item.id} item={item} variant="compact" />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full bg-accent-50">
        <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">关于妖精漫画</h2>
              <p className="mt-4 text-sm leading-loose text-foreground-600 md:text-base">
                妖精漫画是一个专注妖精题材的内容平台。我们收录精灵、妖怪、魔法生物与花之精等不同类型的作品，
                并持续整理相关资讯与行业动态，希望让每一位喜欢奇幻故事的读者，都能轻松找到属于自己的那一部。
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-accent-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line"></i>
                了解更多
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "ri-book-2-line", value: "680+", label: "收录作品" },
                { icon: "ri-group-line", value: "18万", label: "月活读者" },
                { icon: "ri-newspaper-line", value: "1200+", label: "资讯发布" },
                { icon: "ri-hand-heart-line", value: "40+", label: "合作作者" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className={`${s.icon} text-xl`}></i>
                  </span>
                  <span className="font-heading text-2xl text-foreground-950">{s.value}</span>
                  <span className="text-xs text-foreground-500">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}